import { useState } from "react";
import { CaretDown } from "@phosphor-icons/react";
import { isPrimeAreaLocation } from "./helpers";

export function RateBasisBanner({ inputs, setField }) {
    const prime = !!inputs.primeArea;
    const detected = isPrimeAreaLocation(inputs.address_parts);
    const mismatch = detected === true && !prime;
    const [expanded, setExpanded] = useState(false);

    /* Prominent warning only when a Prime-Area address is detected but Standard
     * is selected — the one moment this control should dominate the screen. */
    if (mismatch) {
        return (
            <div
                data-testid="rate-basis-banner"
                className="rounded-[6px] border-2 border-amber-400 bg-amber-50 p-4"
            >
                <div
                    className="flex items-start gap-2 text-sm text-amber-900"
                    data-testid="prime-mismatch-warning"
                >
                    <span className="font-bold leading-none mt-0.5 text-base">!</span>
                    <div className="flex-1">
                        <div className="font-bold">Prime-Area market detected — Standard rates selected</div>
                        <div className="text-xs mt-1">
                            <b>{inputs.address_parts?.city}, {inputs.address_parts?.state}</b> bills ~15–20% higher.
                            This is exactly the miss that underpriced labour ~18% on a closed job.
                        </div>
                    </div>
                    <button
                        onClick={() => setField("primeArea", true)}
                        className="btn btn-primary btn-sm shrink-0"
                        data-testid="apply-prime-btn"
                    >
                        Switch to Prime
                    </button>
                </div>
            </div>
        );
    }

    /* Default state — a small inline chip under the address field. */
    return (
        <div data-testid="rate-basis-banner">
            <button
                type="button"
                onClick={() => setExpanded((e) => !e)}
                className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-[var(--rom-border-strong)] bg-white text-[11px] hover:border-[var(--rom-navy)] transition-colors"
                data-testid="rate-basis-chip"
                aria-expanded={expanded}
            >
                <span className={`w-1.5 h-1.5 rounded-full ${prime ? "bg-[var(--rom-orange)]" : "bg-[var(--rom-navy)]"}`} />
                <span className="font-semibold" data-testid="rate-basis-label">{prime ? "Prime rates" : "Standard rates"}</span>
                <span className="text-[var(--rom-text-3)]">
                    · {detected != null ? "auto-detected from address" : "tap to choose basis"}
                </span>
                <CaretDown size={11} weight="bold" className={`text-[var(--rom-text-3)] transition-transform ${expanded ? "rotate-180" : ""}`} />
            </button>
            {expanded && (
                <div className="inline-flex ml-2 rounded-[4px] border border-[var(--rom-border-strong)] overflow-hidden align-middle">
                    <button
                        onClick={() => setField("primeArea", false)}
                        className={`px-3 py-1 text-[11px] font-bold transition-colors ${!prime ? "bg-[var(--rom-navy)] text-white" : "bg-white text-[var(--rom-text)] hover:bg-slate-50"}`}
                        data-testid="rate-basis-standard"
                    >
                        Standard
                    </button>
                    <button
                        onClick={() => setField("primeArea", true)}
                        className={`px-3 py-1 text-[11px] font-bold transition-colors border-l border-[var(--rom-border-strong)] ${prime ? "bg-[var(--rom-orange)] text-white" : "bg-white text-[var(--rom-text)] hover:bg-slate-50"}`}
                        data-testid="rate-basis-prime"
                    >
                        Prime
                    </button>
                </div>
            )}
        </div>
    );
}
